import analyticsService from '@lib/analytics/AnalyticsService'
import googleAdsService from '@lib/analytics/googleAds/GoogleAdsService'
import { useEffectAsync } from '@lib/hooks/useEffectAsync'
import type { NextRouter } from 'next/router'
import { useRouter } from 'next/router'
import { useRef } from 'react'

/**
 * Converts router query params to URLSearchParams entries.
 */
const queryToEntries = (
    query: NextRouter['query'],
): Array<[string, string]> => {
    const entries: Array<[string, string]> = []
    Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined) {
            const stringValue = Array.isArray(value) ? value.join(',') : value
            entries.push([key, stringValue])
        }
    })
    return entries
}

/**
 * Initializes analytics service and tracks page views on route changes.
 *
 * Features:
 * - Initializes analytics service once with query params
 * - Tracks page views on route changes
 * - Prevents duplicate tracking for the same path
 */
export const useAnalytics = () => {
    const router = useRouter()
    const lastTrackedPath = useRef<string | null>(null)

    // Initialize analytics services
    useEffectAsync(async () => {
        const queryEntries = queryToEntries(router.query)
        const searchParams = new URLSearchParams(queryEntries)
        await analyticsService.initialize(searchParams)

        // Initialize Google Ads direct tracking (if enabled)
        googleAdsService.initialize()
    }, [])

    // Track page views
    useEffectAsync(async () => {
        const fullPath = router.asPath
        if (!fullPath) return

        // Skip if it's the same path we already tracked
        if (fullPath === lastTrackedPath.current) return

        // Split path and query string - parse from asPath directly
        // because router.query can be empty on initial render
        const [pathname, queryString] = fullPath.split('?')

        // Parse query params from the URL string
        const searchParams = new URLSearchParams(queryString || '')
        const queryParams: Record<string, string> = {}
        searchParams.forEach((value, key) => {
            queryParams[key] = value
        })

        await analyticsService.trackPageView({
            pathname,
            queryParams,
            fullUrl: fullPath,
        })

        // Push to GTM dataLayer for virtual page views (SPA navigations)
        if (typeof window !== 'undefined') {
            window.dataLayer?.push({
                event: 'virtual_page_view',
                page_path: pathname,
                page_location: window.location.href,
                page_title: document.title,
            })
        }

        lastTrackedPath.current = fullPath
    }, [router.asPath])
}
